/**
 * Sheets are MUI Drawers customized for Aeronet.
 */
import React, { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha, SxProps, Theme } from "@mui/material/styles";

import { CancelButton, SaveFab } from "./Buttons";
import { Title } from "./Header";
import RecordActionsKebab, { RecordActionsKebabProps } from "./RecordActionsKebab";

/**
 * Sheet widths, owned here so callers ask for a SIZE instead of overriding
 * `.MuiDrawer-paper` width in `sx` (DESIGN-PATTERNS "Styling ownership": a
 * caller supplies a supported variant, not appearance). `default` is the
 * quarter-screen quick-edit sheet; `wide` holds a record's detail tables
 * (Aircraft Roster's day panel, M13-L3: "remove the scroll - make the slide
 * in bigger"). Both are full width below `md`.
 */
export const SHEET_WIDTHS = {
  default: { xs: "100%", sm: "100%", md: "33%", lg: "25%", xl: "25%" },
  wide: { xs: "100%", sm: "100%", md: "75%", lg: "56%", xl: "48%" },
} as const;

export type SheetSize = keyof typeof SHEET_WIDTHS;

export interface SheetProps {
  children?: React.ReactNode;
  "data-testid"?: string;
  open: boolean;
  onClose: () => void;
  /** Width variant — see SHEET_WIDTHS. Defaults to `default`. */
  size?: SheetSize;
  /**
   * The slide-in's accessible name: with it, the sheet is announced as a dialog by that
   * name (what a centred Dialog it replaces was announced as). Without it, unchanged.
   */
  dialogLabel?: string;
  sx?: SxProps<Theme>;
}

const Sheet: React.FC<React.PropsWithChildren<SheetProps>> = (props) => {
  const { children, onClose, open, size = "default", dialogLabel, sx = [] } = props;
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Drawer
      variant="temporary"
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: isMobile ? true : false, // Better open performance on mobile.
      }}
      PaperProps={dialogLabel ? { role: "dialog", "aria-label": dialogLabel } : undefined}
      sx={[
        {
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SHEET_WIDTHS[size],
            overflowX: "hidden",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: alpha(theme.palette.text.primary, 0.15),
            },
            "&:hover fieldset": {
              borderColor: alpha(theme.palette.text.primary, 0.25),
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      aria-labelledby="sheet-title"
      aria-describedby="sheet-content"
      data-testid={props["data-testid"]}
    >
      {children}
    </Drawer>
  );
};

export interface SheetHeaderProps {
  children?: React.ReactNode;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** The record's Print ⋮, left of the close X — the record header's
   *  `recordActions` (RecordActionsKebab, BP-09) in a slide-in. */
  recordActions?: RecordActionsKebabProps;
  sx?: SxProps<Theme>;
}

export const SheetHeader: React.FC<
  React.PropsWithChildren<SheetHeaderProps>
> = (props) => {
  const { children, subtitle, ...otherProps } = props;

  return (
    <>
      <SheetTitle {...otherProps} subtitle={subtitle} />
      <Box pt={2} px={4}>
        {children}
      </Box>
      <Divider
        variant="middle"
        sx={{
          mx: 4,
          py: 1,
          mb: 1,
          borderBottomWidth: 2,
          borderColor: "text.secondary",
        }}
      />
    </>
  );
};

export interface SheetTitleProps {
  onClose: () => void;
  sx?: SxProps<Theme>;
  title: string;
  subtitle?: string;
  recordActions?: RecordActionsKebabProps;
}

export const SheetTitle: React.FC<SheetTitleProps> = (props) => {
  const { onClose, sx = [], title, subtitle, recordActions } = props;

  const close = (
    <IconButton
      edge="end"
      color="inherit"
      onClick={onClose}
      aria-label="close"
      size="small"
      style={{
        marginRight:"15px",
        marginTop:"35px"
      }}
      sx={{ alignSelf: "flex-start", p: 2 }}
    >
      <CloseIcon />
    </IconButton>
  );

  return (
    <Box
      id="sheet-title"
      sx={{
        display: "flex",
        justifyContent: "space-between",
      }}
      pl={4}
    >
      <Box sx={{ pt: 6 }}>
        <Title sx={[...(Array.isArray(sx) ? sx : [sx])]}>
          {title}
        </Title>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {recordActions ? (
        /* The ⋮ beside the X, on the X's line; without recordActions the
           title row is unchanged. */
        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
          <Box sx={{ mt: "45px" }}>
            <RecordActionsKebab {...recordActions} />
          </Box>
          {close}
        </Box>
      ) : (
        close
      )}
    </Box>
  );
};

export interface SheetContentProps {
  children?: React.ReactNode;
}

export const SheetContent: React.FC<
  React.PropsWithChildren<SheetContentProps>
> = (props) => {
  const { children } = props;

  return (
    <Box
      id="sheet-content"
      flex="1 1 auto"
      overflow="auto"
      height="100%"
      pt={2}
      px={4}
    >
      {children}
    </Box>
  );
};

export interface SheetActionsProps {
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * A slide-in's action row is a STICKY FOOTER: always in view, however long the form
 * (Aaron 2026-10-14 on Shelley's SOP, "Sticky footer"). Owned here so every slide-in
 * gets it: SheetActions after SheetContent, and SheetActionsButton / SheetSaveBar last
 * inside it. The paper behind it, so scrolled fields pass under it, and a rule above it.
 */
const STICKY_FOOTER_SX = {
  position: "sticky",
  bottom: 0,
  zIndex: 1,
  bgcolor: "background.paper",
  borderTop: 1,
  borderColor: "divider",
} as const;

export const SheetActions: React.FC<
  React.PropsWithChildren<SheetActionsProps>
> = (props) => {
  const { children } = props;

  return (
    <Box
      data-testid="sheet-footer"
      flex="0 0 auto"
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      p={4}
      sx={STICKY_FOOTER_SX}
    >
      {children}
    </Box>
  );
};

export const SheetActionsButton: React.FC<
  React.PropsWithChildren<SheetActionsProps>
> = (props) => {
  const { children, sx } = props;

  return (
    <Box
      data-testid="sheet-footer"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        width: "100%",
        ...STICKY_FOOTER_SX,
        mt: 2,
        pt: 1.5,
        pb: 2,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export interface SheetHeaderItemProps {
  label: string;
  content: string;
}

export const SheetHeaderItem: React.FC<SheetHeaderItemProps> = (props) => {
  const { label, content } = props;

  return (
    <Typography>
      <Box component="span" fontWeight={500}>
        {label}:
      </Box>{" "}
      {content}
    </Typography>
  );
};

export default Sheet;

/** A slide-in's destructive action (Delete), confirmed before it runs. */
export interface SheetDangerAction {
  label: string;
  onConfirm: () => void | Promise<void>;
  disabled?: boolean;
  /** Why it is disabled — its tooltip. */
  disabledReason?: string;
  confirmTitle: string;
  confirmText?: React.ReactNode;
  /** The confirm's own button; defaults to `label`. */
  confirmLabel?: string;
}

export interface SheetSaveBarProps {
  /** Cancel / Close: closes, writes nothing. */
  onCancel: () => void;
  cancelLabel?: string;
  /** SAVE & CLOSE. With `onSave` absent: the one primary button (`primaryLabel`). */
  onSaveClose?: () => void;
  /** SAVE: writes and stays open. */
  onSave?: () => void;
  /** The one primary's label when there is no SAVE (a create sheet's "Create"). */
  primaryLabel?: string;
  /** The one primary submits its <form> (Formik) instead of calling onSaveClose. */
  submit?: boolean;
  /** The save buttons greyed — invalid, or a save in flight. */
  disabled?: boolean;
  /** Read-only: no save buttons at all, Cancel reads Close. */
  readOnly?: boolean;
  /** Beside the buttons: what the save covers (a "3 days" chip). */
  info?: React.ReactNode;
  /** Bottom-LEFT, in the error colour, behind a confirm. */
  danger?: SheetDangerAction;
  /** Bottom-LEFT, a record-level action that destroys nothing (Reactivate). */
  secondary?: { label: string; onClick: () => void; disabled?: boolean };
}

/**
 * A slide-in's ONE row of actions, at the BOTTOM of the sheet, under the form
 * (Aaron 2026-10-11, on the Pilot Roster's cell sheet: "move save and close to
 * the bottom to follow pattern / remove actions"; Blank BP-89):
 *
 *   [Delete]                      [info]  Cancel  SAVE  SAVE & CLOSE
 *
 * No ACTIONS menu in a slide-in: a destructive item is the Delete here, behind
 * a confirm; a scope choice ("Edit this day only") is a control in the form.
 * Place it LAST in SheetContent; it sticks to the sheet's bottom edge.
 * Callers supply what happens; the row's look is owned here.
 */
export function SheetSaveBar({
  onCancel,
  cancelLabel,
  onSaveClose,
  onSave,
  primaryLabel = "Save",
  submit = false,
  disabled = false,
  readOnly = false,
  info,
  danger,
  secondary,
}: SheetSaveBarProps) {
  const [asking, setAsking] = useState(false);
  return (
    /* STICKY to the bottom of SheetContent (the sheet's scroller): it sits at
       the end of the form, and stays in view while a long sheet scrolls
       (SheetActionsButton's own footer look). */
    <SheetActionsButton>
      <Box data-testid="sheet-save-bar" sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", flexWrap: "wrap" }}>
        {danger && (
          <Tooltip title={danger.disabled ? danger.disabledReason ?? "" : ""}>
            <span>
              <Button color="error" onClick={() => setAsking(true)} disabled={danger.disabled}>
                {danger.label}
              </Button>
            </span>
          </Tooltip>
        )}
        {secondary && (
          <Button onClick={secondary.onClick} disabled={secondary.disabled}>
            {secondary.label}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {info}
        <CancelButton onClick={onCancel}>{cancelLabel ?? (readOnly ? "Close" : "Cancel")}</CancelButton>
        {!readOnly &&
          (onSave ? (
            <SaveFab onClick={() => onSaveClose?.()} onClick2={onSave} disabled={disabled} />
          ) : (
            <Button
              variant="contained"
              type={submit ? "submit" : "button"}
              onClick={submit ? undefined : () => onSaveClose?.()}
              disabled={disabled}
            >
              {primaryLabel}
            </Button>
          ))}
      </Box>
      {danger && (
        <Dialog open={asking} onClose={() => setAsking(false)} data-testid="sheet-danger-confirm">
          <DialogTitle>{danger.confirmTitle}</DialogTitle>
          {danger.confirmText && (
            <DialogContent>
              <DialogContentText>{danger.confirmText}</DialogContentText>
            </DialogContent>
          )}
          <DialogActions>
            <CancelButton onClick={() => setAsking(false)} />
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                setAsking(false);
                void danger.onConfirm();
              }}
            >
              {danger.confirmLabel ?? danger.label}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </SheetActionsButton>
  );
}
